import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BanknoteIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import {
  listPayrollOwed,
  payrollOwedQueryKey,
  type PayrollDateRangeFilters,
} from '@/lib/api/payroll';

import { PayrollDateFilter } from './payroll-date-filter';
import { PayrollHistoryList } from './payroll-history-list';
import { PayrollOwedRow } from './payroll-owed-row';
import type { PayrollView } from './types';

export function Payroll() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const [dateFilter, setDateFilter] = useState<PayrollDateRangeFilters>({});
  const [view, setView] = useState<PayrollView>('owed');

  const owedQuery = useQuery({
    queryKey: payrollOwedQueryKey(businessId ?? '', dateFilter),
    queryFn: () => listPayrollOwed(businessId!, dateFilter),
    enabled: Boolean(businessId) && view === 'owed',
  });

  const owed = owedQuery.data ?? [];

  function detailLink(employeeId: string) {
    const params = new URLSearchParams();
    if (dateFilter.from) params.set('from', dateFilter.from);
    if (dateFilter.to) params.set('to', dateFilter.to);
    const query = params.toString();
    return `/payroll/${employeeId}${query ? `?${query}` : ''}`;
  }

  return (
    <div className="max-w-4xl mx-auto flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <BanknoteIcon className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-xl font-semibold leading-tight">{t.payrollTitle}</h1>
        </div>

        <div className="flex gap-1 border-b">
          {(['owed', 'history'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={[
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                view === v
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              ].join(' ')}
              data-testid={`payroll-view-${v}`}
            >
              {v === 'owed' ? t.payrollViewOwed : t.payrollViewHistory}
            </button>
          ))}
        </div>
      </div>

      {view === 'owed' ? (
        <>
          <PayrollDateFilter value={dateFilter} onChange={setDateFilter} />

          {owedQuery.isPending ? (
            <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
          ) : owed.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">{t.payrollEmpty}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {owed.map((row) => (
                <li key={row.employee_id}>
                  <PayrollOwedRow owed={row} to={detailLink(row.employee_id)} />
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        businessId && <PayrollHistoryList businessId={businessId} />
      )}
    </div>
  );
}
